import React from 'react'
import PropTypes from 'prop-types'
import styles from './DataTable.module.scss'

const cx = (...p) => p.filter(Boolean).join(' ')

const Caret = ({ direction }) => (
  <span className={styles.caret} aria-hidden="true">
    {direction === 'desc' ? '▾' : '▴'}
  </span>
)

const DataTable = ({
  caption,
  columns,
  rows,
  getRowId,
  sort,
  onSortChange,
  onRowClick,
  selectedIds,
  emptyState,
  className,
}) => {
  const selected = new Set(selectedIds || [])

  const handleKeyDown = (e, row) => {
    if (!onRowClick) return

    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      onRowClick(row)
    }
  }

  return (
    <div className={cx(styles.wrap, className)}>
      <table className={styles.table}>
        {caption && <caption className={styles.srOnly}>{caption}</caption>}
        <thead>
          <tr>
            {columns.map(col => {
              const isSorted = sort && sort.key === col.key
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={cx(styles.th, isSorted && styles.sorted)}

                  aria-sort={isSorted ? (sort.direction === 'desc' ? 'descending' : 'ascending') : undefined}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.sortable && onSortChange ? (
                    <button
                      type="button"
                      className={styles.sortButton}
                      onClick={() =>
                        onSortChange({
                          key: col.key,
                          direction: isSorted && sort.direction === 'asc' ? 'desc' : 'asc',
                        })
                      }
                    >
                      {col.header}
                      <Caret direction={isSorted ? sort.direction : 'asc'} />
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && emptyState ? (
            <tr>
              <td className={cx(styles.td, styles.empty)} colSpan={columns.length}>
                {emptyState}
              </td>
            </tr>
          ) : (
            rows.map(row => {
              const id = getRowId(row)
              const isSelected = selected.has(id)
              return (
                <tr
                  key={id}
                  className={cx(styles.row, onRowClick && styles.clickable, isSelected && styles.selected)}
                  aria-selected={selectedIds ? isSelected : undefined}

                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? e => handleKeyDown(e, row) : undefined}
                >
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className={cx(styles.td, col.numeric && styles.numeric, col.mono && styles.mono)}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

DataTable.propTypes = {
  caption: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    header: PropTypes.node.isRequired,
    render: PropTypes.func,
    sortable: PropTypes.bool,

    numeric: PropTypes.bool,

    mono: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  rows: PropTypes.array.isRequired,
  getRowId: PropTypes.func.isRequired,
  sort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }),
  onSortChange: PropTypes.func,
  onRowClick: PropTypes.func,
  selectedIds: PropTypes.array,

  emptyState: PropTypes.node,
  className: PropTypes.string,
}

export default DataTable
